import type { IApiClient } from '../api'
import { useArenaStore } from '../store'
import {
  BrowserWorldStateClient,
  type WorldStateProjection,
} from './worldStateClient'
import type {
  WorldStateErrorResponse,
  WorldStateInterest,
  WorldStateMutationReceipt,
} from '../../shared/durableWorldState'

type ConnectionState = 'connecting' | 'connected' | 'degraded' | 'disconnected'

export interface RuntimeWorldStateClient {
  connect(
    interest: WorldStateInterest,
    onProjection: (projection: WorldStateProjection) => void,
    onReceipt?: (receipt: WorldStateMutationReceipt) => void,
    onError?: (error: WorldStateErrorResponse) => void,
  ): void
  disconnect(): void
}

export interface WorldStateRuntimeOptions {
  apiClient: IApiClient
  apiGeneration: number
  createClient?: (
    onConnectionState: (state: ConnectionState) => void,
  ) => RuntimeWorldStateClient
}

/** Own one durable projection client for one authenticated application owner. */
export function startWorldStateRuntime(options: WorldStateRuntimeOptions): () => void {
  const ownsProjection = (): boolean => {
    const state = useArenaStore.getState()
    return state.apiClient === options.apiClient &&
      state.apiGeneration === options.apiGeneration
  }
  const onConnectionState = (connectionState: ConnectionState): void => {
    if (!ownsProjection()) return
    useArenaStore.getState().setWorldStateStatus(
      connectionState === 'disconnected'
        ? 'disconnected'
        : connectionState === 'degraded'
          ? 'degraded'
          : 'connecting',
    )
  }
  const client = options.createClient?.(onConnectionState) ??
    new BrowserWorldStateClient({ onConnectionState })
  useArenaStore.getState().setWorldStateStatus('connecting')
  client.connect(
    { limit: 500 },
    (projection) => {
      if (ownsProjection()) {
        useArenaStore.getState().setWorldStateProjection(projection)
      }
    },
    () => {},
    () => {
      if (ownsProjection()) useArenaStore.getState().setWorldStateStatus('degraded')
    },
  )
  return () => client.disconnect()
}
